import { Standort as ContextStandort } from '@/features/standortContext/entities/standort';
import { ROTATIONSPLAN_STORAGE_KEY } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';

type PersistedStandort = { id: string };

/**
 * Reads the persisted Standort filter selection (Planung + Übersicht) from
 * localStorage and returns the union of selected Standort-UIDs. Returns null
 * when no selection has been persisted yet, so callers can fall back to the
 * current Standort.
 */
export function getPersistedFilterStandortUids(): string[] | null {
  try {
    const raw = localStorage.getItem(ROTATIONSPLAN_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      state?: {
        ui?: {
          kalender?: { filter?: { standorte?: PersistedStandort[] } };
          uebersicht?: { filter?: { standorte?: PersistedStandort[] } };
        };
      };
    };

    const kalender = parsed.state?.ui?.kalender?.filter?.standorte ?? [];
    const uebersicht = parsed.state?.ui?.uebersicht?.filter?.standorte ?? [];
    const uids = [...new Set([...kalender, ...uebersicht].map((s) => s.id))];

    return uids.length > 0 ? uids : null;
  } catch {
    return null;
  }
}

/**
 * Maps selected Standort-UIDs (filter id space) to the id used by the
 * Rotationspläne fetch (StandortContext id space). Standorte the manager has no
 * access to are dropped, since their Rotationspläne cannot be fetched anyway.
 */
export function resolveFetchStandortIds(
  selectedStandortUids: string[],
  standorte: Pick<ContextStandort, 'id' | 'uid'>[],
): string[] {
  const uidToId = new Map(standorte.map((s) => [s.uid, s.id]));

  return [...new Set(selectedStandortUids)]
    .map((uid) => uidToId.get(uid))
    .filter((id): id is string => id !== undefined);
}
